import { useState, useEffect, useRef, FormEvent, MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  Backdrop,
  Dialog,
  ModalBody,
  CloseButton,
  Title,
  Subtitle,
  Form,
  PrimaryInput,
  PhoneInputRow,
  CountryCodeSelect,
  PhoneInput,
  PrimaryButton,
  Divider,
  SocialButtons,
  SocialButton,
  LegalNote,
  PrivacyLink,
  ErrorMessage,
  CodeInput,
  BackLink,
  ResendRow,
  ResendButton,
  EmailHint,
} from "./styles";
import { useAuth } from "../../contexts/AuthContext";
import {
  initAppleSdk,
  loadAppleSdk,
  signInWithApple,
} from "../../services/appleAuth";
import { loadGoogleSdk, signInWithGoogle } from "../../services/googleAuth";
import { ApiError } from "../../services/apiClient";
import {
  getEmailErrorKey,
  requestEmailCode,
  verifyEmailCode,
} from "../../services/emailAuth";
import {
  formatE164,
  getSmsErrorKey,
  isValidCnMobile,
  requestSmsCode,
  verifySmsCode,
} from "../../services/smsAuth";
import { getCooldownRemaining, markSent } from "../../utils/authCooldown";

type ModalStep = "email" | "code";

const COUNTRY_CODES = [
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+852", label: "🇭🇰 +852" },
  { code: "+853", label: "🇲🇴 +853" },
  { code: "+886", label: "🇹🇼 +886" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
];

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const CloseGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const LoginModal = ({ open, onClose }: LoginModalProps) => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const isChinese = i18n.language === "cn";
  const privacyHref = isChinese ? "/privacy-CN" : "/privacy-EN";
  const [step, setStep] = useState<ModalStep>("email");
  const [emailValue, setEmailValue] = useState("");
  const [codeValue, setCodeValue] = useState("");
  const [countryCode, setCountryCode] = useState("+86");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Esc key closes the modal (but not while a sign-in is in flight).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, loading]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Lazy-load Apple + Google JS SDKs when the modal opens.
  useEffect(() => {
    if (!open) return;
    loadAppleSdk()
      .then(() => initAppleSdk())
      .catch(() => {});
    if (!isChinese) {
      loadGoogleSdk().catch(() => {});
    }
  }, [open, isChinese]);

  // Reset to initial state every time the modal reopens.
  useEffect(() => {
    if (open) {
      setStep("email");
      setEmailValue("");
      setCodeValue("");
      setCountryCode("+86");
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // Auto-focus the code input when we transition to the code step.
  useEffect(() => {
    if (step === "code") {
      // Small delay so the DOM update lands first.
      const id = setTimeout(() => codeInputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [step]);

  // Wall-clock tick — drives the cooldown countdown on the Send/Resend
  // buttons. Only runs while the modal is open. The 500ms cadence keeps
  // the displayed seconds accurate without hammering React.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [open]);

  if (!open) return null;

  // E.164 phone (e.g. "+8613812345678") used by the SMS endpoints when
  // isChinese; derived from `countryCode` + `emailValue` (which doubles as
  // the phone-input field in the CN locale).
  const phoneE164 = formatE164(countryCode, emailValue);

  // Identifier used as the cooldown key — phone in CN, email otherwise.
  const cooldownIdentifier = isChinese ? phoneE164 : emailValue;
  const cooldownRemainingMs = getCooldownRemaining(cooldownIdentifier, now);
  const cooldownActive = cooldownRemainingMs > 0;
  const cooldownSeconds = Math.ceil(cooldownRemainingMs / 1000);
  const cooldownLabel = cooldownActive
    ? t("resendAvailableInSeconds", { seconds: cooldownSeconds })
    : "";

  // ─── Email/Phone step: request code ────────────────────────────────
  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (cooldownActive) return; // belt-and-braces; button is also disabled
    if (isChinese) {
      // Backend only wires +86 right now. Surface a clean error instead of
      // round-tripping to a 422.
      if (countryCode !== "+86" || !isValidCnMobile(emailValue)) {
        setError(t("phoneInvalidFormat"));
        return;
      }
      setLoading(true);
      try {
        await requestSmsCode(phoneE164);
        markSent(phoneE164);
        setNow(Date.now()); // refresh cooldown derivation immediately
        setCodeValue("");
        setStep("code");
      } catch {
        setError(t("loginNetworkError"));
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      await requestEmailCode(emailValue);
      markSent(emailValue);
      setNow(Date.now());
      setCodeValue("");
      setStep("code");
    } catch {
      setError(t("loginNetworkError"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Code step: verify code ────────────────────────────────────────
  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = isChinese
        ? await verifySmsCode(phoneE164, codeValue)
        : await verifyEmailCode(emailValue, codeValue);
      auth.login(result.token, result.user);
      auth.refreshProfile();
      onClose();
    } catch (err) {
      setError(t(isChinese ? getSmsErrorKey(err) : getEmailErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldownActive) return;
    setError(null);
    setLoading(true);
    try {
      if (isChinese) {
        await requestSmsCode(phoneE164);
        markSent(phoneE164);
      } else {
        await requestEmailCode(emailValue);
        markSent(emailValue);
      }
      setNow(Date.now());
    } catch {
      setError(t("loginNetworkError"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setCodeValue("");
    setError(null);
  };

  // Restrict code input to digits only, max 4.
  const handleCodeChange = (val: string) => {
    setCodeValue(val.replace(/\D/g, "").slice(0, 4));
  };

  // ─── Apple / social handlers ───────────────────────────────────────
  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await loadAppleSdk();
      initAppleSdk();
      const result = await signInWithApple();
      auth.login(result.token, result.user);
      auth.refreshProfile();
      onClose();
    } catch (err) {
      if (
        err &&
        typeof err === "object" &&
        "error" in (err as Record<string, unknown>) &&
        (err as { error?: string }).error === "popup_closed_by_user"
      ) {
        // no-op — user dismissed the popup
      } else if (err instanceof ApiError) {
        setError(t("loginAppleError"));
      } else if (err instanceof Error) {
        setError(err.message || t("loginAppleError"));
      } else {
        setError(t("loginAppleError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await loadGoogleSdk();
      const result = await signInWithGoogle();
      auth.login(result.token, result.user);
      auth.refreshProfile();
      onClose();
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === "google_prompt_dismissed"
      ) {
        // no-op — user dismissed the prompt
      } else if (err instanceof ApiError) {
        setError(t("loginGoogleError"));
      } else if (err instanceof Error) {
        setError(err.message || t("loginGoogleError"));
      } else {
        setError(t("loginGoogleError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (loading) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // ─── Code-step JSX ──────────────────────────────────────────────────
  const renderCodeStep = () => (
    <>
      <BackLink type="button" onClick={handleBackToEmail} disabled={loading}>
        &larr; {t("emailCodeBack")}
      </BackLink>
      <Title>{isChinese ? t("smsCodeTitle") : t("emailCodeTitle")}</Title>
      <Subtitle>
        {isChinese ? t("smsCodeSubtitle") : t("emailCodeSubtitle")}
      </Subtitle>
      <EmailHint>{isChinese ? phoneE164 : emailValue}</EmailHint>

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <Form onSubmit={handleCodeSubmit}>
        <CodeInput
          ref={codeInputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={4}
          placeholder="0000"
          aria-label={t("emailCodePlaceholder")}
          value={codeValue}
          onChange={(e) => handleCodeChange(e.target.value)}
          disabled={loading}
          required
        />
        <PrimaryButton type="submit" disabled={loading || codeValue.length < 4}>
          {loading ? t("emailCodeVerifying") : t("emailCodeVerify")}
        </PrimaryButton>
      </Form>

      <ResendRow>
        <ResendButton
          type="button"
          onClick={handleResend}
          disabled={loading || cooldownActive}
        >
          {cooldownActive ? cooldownLabel : t("emailCodeResend")}
        </ResendButton>
      </ResendRow>
    </>
  );

  // ─── Email-step JSX ────────────────────────────────────────────────
  const renderEmailStep = () => (
    <>
      <Title>{t("Log in or sign up")}</Title>
      <Subtitle>
        {isChinese
          ? t("We'll text you a verification code")
          : t("We'll email you a sign-in link")}
      </Subtitle>

      <Form onSubmit={handleEmailSubmit}>
        {isChinese ? (
          <PhoneInputRow>
            <CountryCodeSelect
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={loading}
              aria-label={t("Country code")}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </CountryCodeSelect>
            <PhoneInput
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t("Phone number")}
              aria-label={t("Phone number")}
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              required
              disabled={loading}
            />
          </PhoneInputRow>
        ) : (
          <PrimaryInput
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t("Email")}
            aria-label={t("Email")}
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            required
            disabled={loading}
          />
        )}
        <PrimaryButton type="submit" disabled={loading || cooldownActive}>
          {cooldownActive
            ? cooldownLabel
            : isChinese
            ? t("Send code")
            : t("Continue with Email")}
        </PrimaryButton>
      </Form>

      <Divider>{t("or")}</Divider>

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <SocialButtons>
        <SocialButton
          type="button"
          onClick={handleAppleSignIn}
          disabled={loading}
        >
          <AppleIcon />
          {loading ? t("loginAppleConnecting") : t("Continue with Apple")}
        </SocialButton>
        {!isChinese && (
          <SocialButton
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <GoogleIcon />
            {loading ? t("loginGoogleConnecting") : t("Continue with Google")}
          </SocialButton>
        )}
      </SocialButtons>

      <LegalNote>
        <Trans
          i18nKey="loginLegalNote"
          components={{
            privacy: (
              <PrivacyLink
                href={privacyHref}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </LegalNote>
    </>
  );

  return createPortal(
    <Backdrop onClick={handleBackdropClick} role="presentation">
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("Log in or sign up")}
      >
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label={t("Close")}
          disabled={loading}
        >
          <CloseGlyph />
        </CloseButton>
        <ModalBody>
          {step === "code" ? renderCodeStep() : renderEmailStep()}
        </ModalBody>
      </Dialog>
    </Backdrop>,
    document.body
  );
};

export default LoginModal;
